<template>
  <div class="contentwrapper" v-if="currentProduct">
    <div class="content-dark">
      <button class="button-back" v-if="currentOrder.products.length > 1" v-on:click="backToMulti">
        &#10094; Back
      </button>
      <h1>
        {{ currentProduct.magazine_name }}<br />
        <span v-if="currentOrder.order_id">#{{ currentOrder.order_id }}</span>
      </h1>

      <div class="tracker-navigation">
        <button :class="getTabButtonClass('status')" v-on:click="switchTab('status')">
          Status
        </button>
        <button :class="getTabButtonClass('details')" v-on:click="switchTab('details')">
          Order Details
        </button>
      </div>

      <div v-if="showingTracker">
        <tracker :step="currentProductStep" :title="trackerTitle" subtitle="What does this mean?">
          <template v-slot:dialogTitle>
            {{ trackerTitle }}
          </template>
          <template v-slot:dialogBody v-if="currentProductStep == 2">
            <template v-if="currentProduct.fulfilment.next_fulfilment">
              The publisher of your magazine chooses to receive their new order details
              <b>{{ currentProduct.fulfilment.frequencyString }}</b
              >. They will retrieve your order at
              <b>{{ currentProduct.fulfilment.next_fulfilment }}</b
              >.
            </template>
            <template v-else>
              Your order will be passed onto the publisher shortly.
            </template>
            
            <br />
            <br />

            <span v-if="!currentProduct.is_xmas_gift">
              You will receive the first new issue published after your order has been processed by
              the publisher.
            </span>
            <span v-else>
              This is a Christmas Gift Order. We have communicated this to the publisher. Your
              subscription should start with the first issue released after Christmas Day.
            </span>
          </template>
          <template v-slot:dialogBody v-else-if="currentProductStep == 3">
            The publisher retrieved your order on
            <b>{{ currentProduct.sent_to_publisher_at }}</b> and will add you to their subscriber
            list.
            <br />
            <br />
            <span v-if="isNationalGeographic">
              Please note that National Geographic is delivered from the US and it prints and labels
              its editions a significant amount of time ahead of the date they are delivered.
              Accordingly, there can be a long lead time between being added to the subscriber file
              and the first issue of your subscription arriving (the first issue has been known to
              take between 8 and 14 weeks to arrive). Once the first issue arrives, subsequent
              issues will arrive at the expected frequency and without delay.
            </span>
            <span v-else-if="currentProduct.is_xmas_gift">
              This is a Christmas Gift Order. We have communicated this to the publisher. Your
              subscription should start with the first issue released after Christmas Day.
            </span>
            <span v-else>
              You will receive the first new issue published after you are added to the subscriber
              list.
              <br />
              <br />
              We expect the first issue of a weekly title to arrive within 2 to 4 weeks of order, a
              monthly within 4 to 8 weeks. A bi-monthly or quarterly will likely take a little
              longer depending on how recently the previous issue was released.
            </span>
            <br />
            <br />
            If your first issue hasn't arrived after the expected timescale, or you would like to
            find out the specific start date of your subscription please view
            <a v-on:click="showFaq(7)">I would like to contact someone about my subscription</a>
            in the Help section.
          </template>
        </tracker>

        <div v-if="currentProduct.is_xmas_gift" class="xmas-label">
          <img src="@/assets/tree.png" style="width:64px;" />
          <span>CHRISTMAS GIFT ORDER</span>
          <span>First issue will arrive after Christmas</span>
        </div>
      </div>

      <div v-else>
        <table class="order-details">
          <tr>
            <th>Buyer name:</th>
            <td>
              {{ currentProduct.fulfilment.billing.first_name }}
              {{ currentProduct.fulfilment.billing.last_name }}
            </td>
          </tr>
          <tr>
            <th>Product:</th>
            <td>
              {{ currentProduct.magazine_name }}
              <span v-if="currentProduct.offer_type">- {{ currentProduct.offer_type }}</span>
            </td>
          </tr>
          <tr v-if="currentProduct.is_xmas_gift">
            <th>Christmas Gift:</th>
            <td>Yes - Will be delivered after Christmas</td>
          </tr>
          <tr>
            <th>Order date:</th>
            <td>{{ currentOrder.transaction_datetime }}</td>
          </tr>
          <tr>
            <th>Payment method:</th>
            <td>
              {{
                currentProduct.fulfilment.payment_method === "CC" ? "Credit Card" : "Direct Debit"
              }}
            </td>
          </tr>
          <tr>
            <th>Billing address:</th>
            <td>
              {{ currentProduct.fulfilment.billing.title }}
              {{ currentProduct.fulfilment.billing.first_name }}
              {{ currentProduct.fulfilment.billing.last_name }}<br />
              {{ currentProduct.fulfilment.billing.address_line_1 }}<br />
              <span v-if="currentProduct.fulfilment.billing.address_line_2"
                >{{ currentProduct.fulfilment.billing.address_line_2 }}<br
              /></span>
              {{ currentProduct.fulfilment.billing.town }}<br />
              {{ currentProduct.fulfilment.billing.post_code }}<br />
              {{ currentProduct.fulfilment.billing.country }}<br />
            </td>
          </tr>
          <tr>
            <th>Delivery address:</th>
            <td v-if="hasDeliveryAddress">
              {{ currentProduct.fulfilment.delivery.title }}
              {{ currentProduct.fulfilment.delivery.first_name }}
              {{ currentProduct.fulfilment.delivery.last_name }}<br />
              {{ currentProduct.fulfilment.delivery.address_line_1 }}<br />
              <span v-if="currentProduct.fulfilment.delivery.address_line_2"
                >{{ currentProduct.fulfilment.delivery.address_line_2 }}<br
              /></span>
              {{ currentProduct.fulfilment.delivery.town }}<br />
              {{ currentProduct.fulfilment.delivery.post_code }}<br />
              {{ currentProduct.fulfilment.delivery.country }}<br />
            </td>
            <td v-else>No Delivery Details</td>
          </tr>
          <tr v-if="currentProduct.fulfilment.digital_access_link">
            <th>Digital access link:</th>
            <td>
              <a :href="currentProduct.fulfilment.digital_access_link" target="_blank"
                >click here</a
              >
            </td>
          </tr>
        </table>
      </div>

      <div class="more-arrow">
        <img src="@/assets/down-arrow.png" />
        <img src="@/assets/down-arrow.png" />
      </div>
    </div>

    <faqs
      :current-product="currentProduct"
      :order="currentOrder"
      :open-question="currentFaqQuestion"
    >
    </faqs>
  </div>
</template>

<script>
import Tracker from "@/components/Tracker.vue";
import Faqs from "@/components/Faqs.vue";

export default {
  name: "OrderFound",
  components: {
    Tracker,
    Faqs
  },
  props: {},
  data() {
    return {
      tab: "status",
      transaction_ref: this.$route.params.transref,
      productIndex: this.$route.params.productIndex ? parseInt(this.$route.params.productIndex) : 0,
      currentFaqQuestion: null
    };
  },
  watch: {
    currentOrder: function(val) {
      //is a multi cart and hasnt selected the which one to view
      var backToMultiPage =
        !this.$route.params.productIndex && this.currentOrder.products.length > 1;

      // of is single cart and but is single issue/gift or egift so shouldnt be on this page
      if (
        this.currentOrder.products.length === 1 &&
        (this.currentOrder.products[0].campaign_ref.startsWith("4") ||
          this.currentOrder.products[0].campaign_ref.toLowerCase() == "gift" ||
          this.currentOrder.products[0].campaign_ref.toLowerCase() == "egft")
      ) {
        backToMultiPage = true;
      }

      if (backToMultiPage) {
        this.backToMulti();
      }
    },
    orderFound: function(val) {
      if (val === false) {
        this.$router.push("/order-processing");
      }
    }
  },

  mounted() {
    this.$store.dispatch("getOrder", this.transaction_ref);
  },
  methods: {
    showFaq(numberOfFaq) {
      this.currentFaqQuestion = numberOfFaq;
    },
    switchTab(setting) {
      this.tab = setting;
    },
    getTabButtonClass(tabName) {
      return tabName == this.tab ? "button-default" : "button-unselected";
    },
    loadData() {
      //if we load this into the store we wont need to load it on each page switch!
      //https://jsonblob.com/api/jsonBlob/0e159476-80c5-11eb-82da-b9bb2910645d
    },
    backToMulti() {
      this.$router.push("/" + this.transaction_ref + "/order-multi");
    }
  },
  computed: {
    isNationalGeographic() {
      return this.currentProduct.campaign_ref == "NGEZ";
    },
    showingTracker() {
      return this.tab === "status";
    },
    currentProduct() {
      return this.currentOrder !== null ? this.currentOrder.products[this.productIndex] : null;
    },
    currentOrder() {
      var response = this.$store.state.orderResponse;
      return response != null ? this.$store.state.orderResponse.order : null;
    },
    orderFound() {
      return this.$store.state.orderFound;
    },
    currentProductStep() {
      switch (this.currentProduct.statusString) {
        case "SendingToPublisher":
          return 2;
        case "OrderWithPublisher":
          return 3;
        default:
          //OrderPlace
          return 1;
      }
    },
    trackerTitle() {
      switch (this.currentProduct.statusString) {
        case "SendingToPublisher":
          return "Sending to publisher";
        case "OrderWithPublisher":
          return "Order with publisher";
        default:
          //OrderPlace
          return "Processing Order";
      }
    },
    hasDeliveryAddress() {
      return (
        this.currentProduct.fulfilment.delivery.address_line_1.length > 0 ||
        this.currentProduct.fulfilment.delivery.first_name.length > 0 ||
        this.currentProduct.fulfilment.delivery.last_name.length > 0
      );
    }
  }
};
</script>
<style lang="scss">
.order-details {
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  margin: 10px 30px;

  td {
    vertical-align: top;
    padding-left: 5px;
  }

  th {
    vertical-align: top;
    font-weight: inherit;
    white-space: nowrap;
    text-align: left;
  }
}

.xmas-label {
  margin-top: -130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: center;
}
</style>
